import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import LockOpenIcon from '@mui/icons-material/LockOpenOutlined'
import LockIcon from '@mui/icons-material/LockOutlined'
import AddIcon from '@mui/icons-material/AddCircleOutline'
import RemoveIcon from '@mui/icons-material/RemoveCircleOutline'
import PrintIcon from '@mui/icons-material/Print'
import PointOfSaleIcon from '@mui/icons-material/PointOfSale'
import {
  attenduEnCaisse,
  dateHeureLisible,
  format,
  money,
  type CashSession,
  type CurrencyCode,
} from '@iobiz/shared'
import { useHistoriqueCaisse, useMouvementsCaisse, useSessionCaisse } from '../data/hooks.js'
import { useSessionStore } from '../stores/sessionStore.js'
import {
  ajouterMouvement,
  cloturerCaisse,
  ouvrirCaisse,
} from '../data/repositories/caisse.write.js'
import { MontantDialog } from '../features/sales/MontantDialog.js'
import { MouvementDialog } from '../features/caisse/MouvementDialog.js'
import { TicketCloture } from '../features/caisse/TicketCloture.js'
import {
  mesurerPageTicket,
  PortailImpression,
  ReglesImpression,
} from '../features/sales/impression.js'
import { EmptyState } from '../ui/DataTable.js'
import { flushSync } from 'react-dom'

/**
 * La caisse : ouverture, fond, mouvements, clôture.
 *
 * ⭐ Un écran à part de la caisse de VENTE, et c'est délibéré. Celui-ci se
 * tient deux fois par jour, au calme, par qui répond de l'argent du tiroir ;
 * l'autre se tient toute la journée, debout, devant un client qui attend. Les
 * mêler aurait mis « clôturer la caisse » à portée de pouce d'un caissier au
 * milieu d'une vente.
 *
 * ⚠️ L'écart de clôture n'est pas un jugement : il se constate. C'est pour
 * cela que les MOUVEMENTS existent — sans eux, un prélèvement pour la banque
 * apparaît comme un manque, et la caisse accuse le caissier d'une faute qu'il
 * n'a pas commise. C'est le premier reproche fait aux caisses informatisées.
 */
export function CaisseSessionPage(): JSX.Element {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const store = useSessionStore((s) => s.stores.find((b) => b.slug === slug))
  const storeId = store?.storeId
  const devise = (store?.currencyCode ?? 'XAF') as CurrencyCode
  const prix = (v: number): string => format(money(v, devise), 'fr-FR')

  const session = useSessionCaisse(storeId)
  const historique = useHistoriqueCaisse(storeId)
  const mouvements = useMouvementsCaisse(storeId, session?.id)

  const [ouvertureOuverte, setOuvertureOuverte] = useState(false)
  const [clotureOuverte, setClotureOuverte] = useState(false)
  const [mouvementOuvert, setMouvementOuvert] = useState<'apport' | 'prelevement' | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  /** La session à imprimer : celle qu'on vient de clôturer, ou la courante. */
  const [aImprimer, setAImprimer] = useState<CashSession | null>(null)
  const [pageTicket, setPageTicket] = useState<string | null>(null)

  const attendu = session ? attenduEnCaisse(session) : 0

  async function ouvrir(fond: number): Promise<void> {
    if (!storeId) return
    try {
      await ouvrirCaisse(storeId, fond)
      setOuvertureOuverte(false)
      setMessage(`Caisse ouverte avec un fond de ${prix(fond)}.`)
    } catch (e) {
      setMessage(e instanceof Error ? e.message : 'Ouverture impossible.')
    }
  }

  async function mouvementer(montant: number, motif: string): Promise<void> {
    if (!storeId || !session) return
    try {
      await ajouterMouvement(storeId, session.id, montant, motif)
      setMouvementOuvert(null)
      setMessage(
        montant > 0 ? `Apport de ${prix(montant)} enregistré.` : `Prélèvement de ${prix(-montant)} enregistré.`,
      )
    } catch (e) {
      setMessage(e instanceof Error ? e.message : 'Enregistrement impossible.')
    }
  }

  async function cloturer(compte: number): Promise<void> {
    if (!storeId || !session) return
    try {
      const close = await cloturerCaisse(storeId, session.id, compte)
      setClotureOuverte(false)
      setMessage(
        close.variance === 0
          ? 'Caisse clôturée, sans écart.'
          : `Caisse clôturée. Écart : ${prix(close.variance ?? 0)}.`,
      )
      // ⭐ Le rapport part à l'imprimante dans la foulée : c'est le document
      // qu'on agrafe au fond de caisse du lendemain, et le réclamer d'un
      // second geste, c'est l'oublier un soir sur deux.
      imprimer(close)
    } catch (e) {
      setMessage(e instanceof Error ? e.message : 'Clôture impossible.')
    }
  }

  /**
   * ⚠️ Deux rendus synchrones, comme pour le ticket de vente : le document
   * doit exister pour être mesuré, et sa hauteur être connue avant l'appel.
   * `window.print()` fige la page telle qu'elle est à cet instant.
   */
  function imprimer(quoi: CashSession): void {
    flushSync(() => {
      setAImprimer(quoi)
      setPageTicket(null)
    })
    const lancer = (): void => {
      flushSync(() => setPageTicket(mesurerPageTicket()))
      window.print()
    }
    if (document.getElementById('ticket')) lancer()
    else requestAnimationFrame(lancer)
  }

  return (
    <Box>
      <ReglesImpression format="ticket" pageTicket={pageTicket} />
      {aImprimer && (
        <PortailImpression>
          <TicketCloture
            session={aImprimer}
            mouvements={aImprimer.id === session?.id ? (mouvements ?? []) : []}
            boutique={store}
          />
        </PortailImpression>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 4, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h1">Caisse</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
            Ouverture, fonds et clôture du tiroir
          </Typography>
        </Box>
        <Box sx={{ flex: 1 }} />
        {session && (
          <Button
            variant="outlined"
            startIcon={<PointOfSaleIcon />}
            onClick={() => navigate(`/s/${slug}/caisse`)}
          >
            Aller encaisser
          </Button>
        )}
      </Box>

      {/* ------------------------------------------------------ caisse fermée */}
      {session === null && (
        <Paper variant="outlined" sx={{ p: 4 }}>
          {/* ⚠️ Le message dit CE QUI EST BLOQUÉ, pas seulement l'état. « Caisse
              fermée » seul laisse le caissier chercher pourquoi la vente est
              refusée deux écrans plus loin. */}
          <Alert severity="warning" sx={{ mb: 4 }}>
            Aucune caisse ouverte : <strong>aucune vente ne peut être encaissée</strong>. Ouvrez la
            caisse en déclarant le fond présent dans le tiroir.
          </Alert>
          <Button
            variant="contained"
            size="large"
            startIcon={<LockOpenIcon />}
            onClick={() => setOuvertureOuverte(true)}
          >
            Ouvrir la caisse
          </Button>
        </Paper>
      )}

      {/* ------------------------------------------------------ caisse ouverte */}
      {session && (
        <Paper variant="outlined" sx={{ p: 4, mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            <Chip label="Caisse ouverte" color="success" />
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Depuis le {dateHeureLisible(session.openedAt)}
              {session.openedByName ? ` · par ${session.openedByName}` : ''}
            </Typography>
          </Box>

          <Table size="small" sx={{ mb: 3, maxWidth: '32rem' }}>
            <TableBody>
              <TableRow>
                <TableCell>Fond de caisse</TableCell>
                <TableCell align="right">{prix(session.openingFloat)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Encaissé en espèces
                  <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                    {session.salesCount ?? 0} vente{(session.salesCount ?? 0) > 1 ? 's' : ''}
                  </Typography>
                </TableCell>
                <TableCell align="right">{prix(session.cashTotal ?? 0)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Apports et prélèvements</TableCell>
                <TableCell align="right">{prix(session.movementsTotal ?? 0)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, borderTop: 2 }}>Attendu en caisse</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, borderTop: 2 }}>
                  {prix(attendu)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button startIcon={<AddIcon />} onClick={() => setMouvementOuvert('apport')}>
              Apport
            </Button>
            <Button startIcon={<RemoveIcon />} onClick={() => setMouvementOuvert('prelevement')}>
              Prélèvement
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button
              variant="contained"
              color="warning"
              startIcon={<LockIcon />}
              onClick={() => setClotureOuverte(true)}
            >
              Clôturer la caisse
            </Button>
          </Box>

          {mouvements && mouvements.length > 0 && (
            <>
              <Divider sx={{ my: 3 }} />
              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                Mouvements de la session
              </Typography>
              <Table size="small">
                <TableBody>
                  {mouvements.map((m) => (
                    <TableRow key={m.id}>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {dateHeureLisible(m.createdAt)}
                      </TableCell>
                      <TableCell>{m.reason}</TableCell>
                      <TableCell sx={{ color: 'text.secondary' }}>{m.createdByName}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                        {m.amount > 0 ? '+' : '−'} {prix(Math.abs(m.amount))}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}
        </Paper>
      )}

      {/* ---------------------------------------------------------- historique */}
      <Typography variant="h2" sx={{ mb: 2 }}>
        Sessions précédentes
      </Typography>
      <Paper variant="outlined">
        {historique && historique.filter((s) => s.status === 'closed').length === 0 ? (
          <EmptyState title="Aucune caisse clôturée" description="Les sessions apparaîtront ici après leur clôture." />
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Ouverte le</TableCell>
                <TableCell>Clôturée le</TableCell>
                <TableCell align="right">Attendu</TableCell>
                <TableCell align="right">Compté</TableCell>
                <TableCell align="right">Écart</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {(historique ?? [])
                .filter((s) => s.status === 'closed')
                .map((s) => (
                  <TableRow key={s.id}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {dateHeureLisible(s.openedAt)}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {s.closedAt ? dateHeureLisible(s.closedAt) : '—'}
                    </TableCell>
                    <TableCell align="right">{prix(s.expectedAmount ?? 0)}</TableCell>
                    <TableCell align="right">{prix(s.closingCounted ?? 0)}</TableCell>
                    {/* ⚠️ L'écart n'est coloré que s'il existe : peindre un zéro
                        en vert transforme une absence d'écart en félicitation,
                        et un écart de 100 en faute. */}
                    <TableCell
                      align="right"
                      sx={{
                        fontWeight: 700,
                        color: (s.variance ?? 0) === 0 ? 'text.primary' : 'warning.main',
                      }}
                    >
                      {prix(s.variance ?? 0)}
                    </TableCell>
                    <TableCell align="right">
                      <Button size="small" startIcon={<PrintIcon />} onClick={() => imprimer(s)}>
                        Rapport
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      <MontantDialog
        open={ouvertureOuverte}
        titre="Ouvrir la caisse"
        description="Comptez ce qui se trouve dans le tiroir avant la première vente. C’est ce montant qui sera déduit à la clôture."
        libelleAction="Ouvrir la caisse"
        devise={devise}
        onClose={() => setOuvertureOuverte(false)}
        onValider={ouvrir}
      />

      <MontantDialog
        open={clotureOuverte}
        titre="Clôturer la caisse"
        description={`Comptez le tiroir et saisissez le total. Attendu : ${prix(attendu)}. L’écart sera constaté et figé.`}
        avertissement="La clôture est définitive : une session clôturée n’accepte plus ni vente ni mouvement. Le rapport s’imprimera aussitôt."
        libelleAction="Clôturer"
        devise={devise}
        onClose={() => setClotureOuverte(false)}
        onValider={cloturer}
      />

      <MouvementDialog
        open={mouvementOuvert !== null}
        sens={mouvementOuvert ?? 'apport'}
        devise={devise}
        onClose={() => setMouvementOuvert(null)}
        onValider={mouvementer}
      />

      <Snackbar
        open={message !== null}
        autoHideDuration={6000}
        onClose={() => setMessage(null)}
        message={message}
      />
    </Box>
  )
}
